import * as R from 'ramda'
import { MouseEvent, ReactNode, memo, useMemo } from 'react'
import { Divider,useTheme } from '@mui/material'
import { RoomTypes } from '@weblite/ts-action/room'
import { EmbeddedLink, IMessage } from '@weblite/ts-action/message'
import { useDraft } from '$logic/draft/react-hook'
import { useRoomPendingMessagesById } from '$logic/pending-messages/react-hook'
import { useRoomPendingUploadsById } from '$logic/pending-uploads/react-hook'
import { useLongPress } from '$hooks/use-long-press'
import { nothing } from '$helper/functions/utils'
import { Title } from './components/title'
import { DateFormatter } from './components/date-formatter'
import { MessageType } from './components/message-type'
import { Message } from './components/message'
import { Status } from './components/status'
import { MessageDraftPrefix } from './components/message-draft'
import './styles.scss'
import { LastMessageSenderName } from '$common/chat-list-item-new/components/last-message-sender-name'
import { temporaryLastMessageStorage } from '$logic/room-last-message/storage'
import { RoomListItem } from '$logic/room-list'

// TODO: CHECK THESE PROPS IF THEY ARE CORRECTLY REQUIRED OR NOT
// TODO: Import MessageType from logic
interface Props {
  chatId: string
  pinned: boolean
  selected: boolean
  roomType?: RoomTypes
  title: string
  verified?: boolean
  date?: number
  lastMessageSenderIcon?: ReactNode
  lastMessageSenderName?: ReactNode
  lastMessageType?: IMessage['type']
  lastMessageBody: string
  fromMe?: boolean
  muted?: boolean
  seen?: boolean
  received?: boolean
  unseen?: number
  eventTitle?: ReactNode
  messageAction?: ReactNode
  avatarElement?: ReactNode
  hasDeleted?: boolean
  hasMarkAsUnread: boolean
  hasMention?: boolean
  isOnTop?: boolean
  isMookeb?: boolean
  isEducational?: boolean
  links?: EmbeddedLink[]
  onClick: (chatId: string, type: RoomListItem['type']) => void
  onContextMenu: (chatId: string) => (e: MouseEvent) => void
  onLongPress?: (chatId: string) => () => void
  isOpened: boolean | undefined
  isGroup?: boolean
}

function BorderElement() {
  const background = useTheme().palette.neutral700

  return (
    <div
      style={{
        width: '4px',
        background,
        height: '100%',
        position: 'absolute',
        right: 0,
      }}
    />
  )
}
// TODO: IMPLEMENT LOGIC FOR EVENT AND DRAFT IN PARENTS
export function ChatListItemBaseComponent({
  chatId,
  pinned,
  selected,
  roomType,
  title,
  verified = false,
  date,
  lastMessageSenderIcon = null,
  lastMessageSenderName = null,
  lastMessageType,
  lastMessageBody,
  fromMe = false,
  muted = false,
  seen = false,
  received = false,
  unseen = 0,
  eventTitle = null,
  messageAction = null,
  avatarElement = null,
  hasDeleted = false,
  hasMarkAsUnread,
  hasMention = false,
  isOnTop,
  isMookeb = false,
  isEducational = false,
  links,
  onClick,
  onContextMenu,
  onLongPress = () => nothing,
  isOpened = false,
  isGroup = false,
}: Props) {
  const theme = useTheme()
  const longPressHandlers = useLongPress(onLongPress?.(chatId) || nothing)

  // TODO : MOVE USE_DRAFT TO CHAT-LIST-ITEM-BASE
  const draftEntity = useDraft(chatId)
  const draft = draftEntity?.value?.body

  const pendingMessages = useRoomPendingMessagesById(chatId)
  const pendingUploads = useRoomPendingUploadsById(chatId)

  const temporaryLastMessage =
    temporaryLastMessageStorage.get(chatId)?.lastMessage

  const pendingContent = useMemo(
    () =>
      R.head(
        R.sort(R.ascend(R.prop('pendingDate')), [
          ...pendingMessages,
          ...pendingUploads,
        ]),
      ),
    [pendingUploads, pendingMessages],
  )

  const lastMessageIsPending =
    pendingContent?.pendingDate && pendingContent?.pendingDate > (date || 0)
  const lastVisibleBody = lastMessageIsPending
    ? pendingContent?.body
    : temporaryLastMessage
      ? temporaryLastMessage?.body
      : lastMessageBody

  const lastVisibleDate = unseen
    ? date
    : draftEntity?.date
      ? draftEntity.date
      : lastMessageIsPending
        ? pendingContent?.pendingDate
        : temporaryLastMessage?.date
          ? temporaryLastMessage.date
          : date

  const lastVisibleMessageType = (
    lastMessageIsPending
      ? pendingContent?.type
      : temporaryLastMessage?.type || lastMessageType
  ) as IMessage['type']

  const showLastMessageSenderInfos = !messageAction && (unseen || !draft)
  const showStatusBar = Boolean(unseen || !draft)

  return (
    <>
      <button
        type="button"
        className="chat-list-item--root"
        onClick={() => onClick(chatId, roomType)}
        onContextMenu={onContextMenu(chatId)}
        //  eslint-disable-next-line react/jsx-props-no-spreading
        {...longPressHandlers}
        style={{
          backgroundColor: selected
            ? theme.palette.primary100
            : isOpened
              ? 'rgba(80, 91, 95, 0.10)'
              : pinned || isOnTop
                ? theme.palette.primary50 // Pinned Color
                : theme.palette.white,
        }}
      >
        {isOpened && <BorderElement />}
        {avatarElement}

        <div className="chat-list-item--body">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '4px',
            }}
          >
            <Title
              roomType={roomType}
              title={title}
              verified={verified}
              hasDeleted={hasDeleted}
              isMookeb={isMookeb}
              isEducational={isEducational}
            />
            {lastVisibleDate && <DateFormatter time={lastVisibleDate} />}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'right',
            }}
          >
            {showLastMessageSenderInfos && (
              <>
                {!(lastMessageIsPending || temporaryLastMessage) &&
                  lastMessageSenderIcon}
                {!(lastMessageIsPending || temporaryLastMessage) &&
                  lastMessageSenderName}
                {(lastMessageIsPending || temporaryLastMessage) && isGroup && (
                  <LastMessageSenderName
                    lastMessage={temporaryLastMessage || pendingContent}
                  />
                )}
                <MessageType type={lastVisibleMessageType} />
              </>
            )}
            {!messageAction && !unseen && <MessageDraftPrefix draft={draft} />}
            {!messageAction &&
              !draft &&
              !lastMessageIsPending &&
              !temporaryLastMessage &&
              eventTitle}

            <Message
              type={lastVisibleMessageType}
              body={lastVisibleBody}
              draft={draft}
              messageAction={messageAction}
              hasUnseen={!!unseen}
              links={links}
            />

            <Status
              fromMe={fromMe}
              pinned={pinned}
              muted={muted}
              seen={seen}
              received={received}
              unseen={unseen}
              isPending={!!lastMessageIsPending}
              showStatusBar={showStatusBar}
              hasMarkAsUnread={hasMarkAsUnread}
              hasMention={hasMention}
            />
          </div>
        </div>
      </button>
      {!pinned && (
        <Divider
          variant={selected ? undefined : 'inset'}
          sx={{
            borderColor: selected ? 'secondary.light' : 'veryVeryLightGray',
          }}
        />
      )}
    </>
  )
}

export const ChatListItemBase = memo(ChatListItemBaseComponent)
